import { AfterViewInit, Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';

@Component({
	standalone: false,
	selector: 'app-guest-count-modal',
	templateUrl: './guest-count-modal.component.html',
	styleUrls: ['./guest-count-modal.component.scss'],
})
export class GuestCountModalComponent implements OnInit, AfterViewInit {
	@Input() min = 1;
	@Input() max = 6;
	@Input() initial = 1;
	numbers: number[] = [];
	current!: number;
	@ViewChild('scrollContainer', { static: true })
	scrollContainer!: ElementRef<HTMLDivElement>;
	@Input() equalSplit: boolean = false;
	private modalCtrl = inject(ModalController);
	private frameRequested = false;

	static async show(
		modalCtrl: ModalController,
		initial: number = 2,
		max: number = 6,
		min: number = 2,
		equalSplit: boolean = false
	): Promise<number | null> {
		const modal = await modalCtrl.create({
			component: GuestCountModalComponent,
			componentProps: {
				initial,
				max,
				min,
				equalSplit,
			},
			showBackdrop: true,
			backdropDismiss: true,
			cssClass: 'guest-count-sheet',
		});

		await modal.present();
		const { data } = await modal.onWillDismiss<number | null>();
		return data ?? null;
	}

	ngOnInit() {
		this.numbers = Array.from({ length: this.max - this.min + 1 }, (_, i) => this.min + i);
		this.current = Math.min(this.max, Math.max(this.min, this.initial));
	}

	ngAfterViewInit() {
		const container = this.scrollContainer.nativeElement;
		requestAnimationFrame(() => this.scrollToValue(this.current, false));
		setTimeout(() => {
			container.addEventListener('scroll', () => {
				if (!this.frameRequested) {
					this.frameRequested = true;
					requestAnimationFrame(() => {
						this.detectClosest();
						this.frameRequested = false;
					});
				}
			});
		}, 1000);
	}

	selectNumber(value: number, smooth = true) {
		this.current = value;
		this.scrollToValue(value, smooth);
	}

	async confirmCenter() {
		await this.modalCtrl.dismiss(this.current);
	}

	async close() {
		await this.modalCtrl.dismiss();
	}

	private detectClosest() {
		const container = this.scrollContainer.nativeElement;
		const center = container.offsetWidth / 2;
		const children = Array.from(container.querySelectorAll('.num')) as HTMLElement[];

		let closest: HTMLElement | null = null;
		let closestDist = Infinity;

		children.forEach(el => {
			const rect = el.getBoundingClientRect();
			const containerRect = container.getBoundingClientRect();
			const offset = rect.left + rect.width / 2 - (containerRect.left + center);
			const dist = Math.abs(offset);
			if (dist < closestDist) {
				closestDist = dist;
				closest = el;
			}
		});

		if (closest) {
			const val = parseInt((closest as HTMLElement).textContent || '0', 10);
			if (val !== this.current) {
				this.current = val;
			}
		}
	}

	private scrollToValue(value: number, smooth = true) {
		const container = this.scrollContainer.nativeElement;
		const items = Array.from(container.querySelectorAll('.num')) as HTMLElement[];

		const idx = this.numbers.indexOf(value);
		if (idx !== -1 && items[idx]) {
			const el = items[idx];
			const targetLeft = el.offsetLeft + el.offsetWidth / 2 - container.clientWidth / 2;

			if (smooth) {
				container.scrollTo({ left: targetLeft, behavior: 'smooth' });
			} else {
				container.scrollLeft = targetLeft;
			}
		}
	}
}
